import { SearchOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Card, DatePicker, Grid, Input, Segmented, Table, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { apiErrorCode, apiErrorMessage } from '../api/client';
import { reportsApi } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type { ProductAnalyticsParams, ProductAnalyticsRow } from '../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/PageStates';
import { useStickyTable } from '../context/StickyTableContext';
import { useStoreContext } from '../context/StoreContext';
import { formatKopeks } from '../lib/money';
import {
  MAX_RANGE_DAYS,
  filterProducts,
  formatBps,
  marginBps,
  parseProductsSearch,
  periodRange,
  productsSearch,
  shareBps,
  type ProductPeriod,
  type ProductStatus,
  type ProductsView,
} from '../lib/productAnalytics';
import { formatQuantityMilli } from '../lib/quantity';
import { ProductDrawer } from './products/ProductDrawer';

const NUMS = { whiteSpace: 'nowrap' as const, fontVariantNumeric: 'tabular-nums' as const };

function Money({ value, signed = false }: { value: number; signed?: boolean }): JSX.Element {
  if (value === 0) return <Typography.Text type="secondary">—</Typography.Text>;
  return (
    <Typography.Text type={signed && value < 0 ? 'danger' : undefined} style={NUMS}>
      {formatKopeks(value)}
    </Typography.Text>
  );
}

function isRangeTooLong(error: unknown): boolean {
  return apiErrorCode(error) === 'RANGE_TOO_LONG' || apiErrorMessage(error) === 'RANGE_TOO_LONG';
}

function Price({ value }: { value: number | null }): JSX.Element {
  if (value === null) return <Typography.Text type="secondary">—</Typography.Text>;
  return <span style={NUMS}>{formatKopeks(value)}</span>;
}

/** A table column plus its totals-row cell; `compact: false` hides it on phones. */
type ColDef = ColumnType<ProductAnalyticsRow> & { summary?: () => ReactNode; compact?: boolean };

/**
 * /products — «Аналитика по товарам»: what the stores took over a period, per
 * product (active quantity, average buy/issue price, profit, наценка, share),
 * searchable and sortable; a tap opens the накладные behind a product. The
 * view (period, window, status) lives in the URL so a накладная's «Назад»
 * lands on the same view. Admin-only (buy prices).
 */
export function ProductsPage(): JSX.Element {
  const { t } = useTranslation();
  const { storeId: ctxStoreId, storeIds: ctxStoreIds } = useStoreContext();
  const sticky = useStickyTable();
  // Phone: four columns (товар / кол-во / прибыль / доля); the rest moves
  // under the product name and into the drawer.
  const compact = !Grid.useBreakpoint().md;
  const [searchParams, setSearchParams] = useSearchParams();
  const view = useMemo(() => parseProductsSearch(searchParams.toString(), dayjs()), [searchParams]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ProductAnalyticsRow | null>(null);

  const setView = (next: ProductsView): void => {
    setSelected(null);
    setSearchParams(productsSearch(next).slice(1), { replace: true });
  };
  const changePeriod = (period: ProductPeriod): void => {
    // «Период» starts from the window on screen; presets re-resolve to now.
    if (period === 'custom') setView({ ...view, period });
    else setView({ ...view, period, ...periodRange(period, dayjs()) });
  };

  const scope = { storeId: ctxStoreId, storeIds: ctxStoreIds };
  const params: ProductAnalyticsParams = { from: view.from, to: view.to, status: view.status };
  const query = useQuery({
    queryKey: queryKeys.productAnalytics(params, scope),
    queryFn: () => reportsApi.products(params, scope),
    placeholderData: keepPreviousData,
  });

  const { rows: filtered, similarOnly } = useMemo(
    () => filterProducts(query.data?.rows ?? [], search),
    [query.data, search],
  );
  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => ({
          quantityMilli: acc.quantityMilli + r.quantityMilli,
          pricedQuantityMilli: acc.pricedQuantityMilli + r.pricedQuantityMilli,
          buySumKopeks: acc.buySumKopeks + r.buySumKopeks,
          pricedBuySumKopeks: acc.pricedBuySumKopeks + r.pricedBuySumKopeks,
          issueSumKopeks: acc.issueSumKopeks + r.issueSumKopeks,
          profitKopeks: acc.profitKopeks + r.profitKopeks,
        }),
        {
          quantityMilli: 0,
          pricedQuantityMilli: 0,
          buySumKopeks: 0,
          pricedBuySumKopeks: 0,
          issueSumKopeks: 0,
          profitKopeks: 0,
        },
      ),
    [filtered],
  );
  // «Доля» is relative to what is on screen: a search narrows the base too.
  const rows = useMemo(
    () =>
      filtered.map((r) => ({ ...r, shareBps: shareBps(r.issueSumKopeks, totals.issueSumKopeks) })),
    [filtered, totals.issueSumKopeks],
  );
  const unpricedMilli = totals.quantityMilli - totals.pricedQuantityMilli;
  const returnTo = `/products${productsSearch(view)}`;

  const allColumns: ColDef[] = [
    {
      title: t('products.colProduct'),
      dataIndex: 'name',
      fixed: compact ? undefined : 'left',
      width: compact ? undefined : 180,
      sorter: (a, b) => a.name.localeCompare(b.name, 'ru'),
      render: (name: string, row) => (
        <div style={{ display: 'grid' }}>
          <Typography.Text strong>{name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {compact
              ? t('products.rowMetaCompact', {
                  buy: row.avgBuyPriceKopeks === null ? '—' : formatKopeks(row.avgBuyPriceKopeks),
                  issue:
                    row.avgIssuePriceKopeks === null ? '—' : formatKopeks(row.avgIssuePriceKopeks),
                  margin: formatBps(row.marginBps),
                })
              : t('products.rowMeta', {
                  docs: row.docCount,
                  stores: row.storeCount,
                  date: dayjs(row.lastDate).format('D MMM'),
                })}
          </Typography.Text>
        </div>
      ),
      summary: () => t('products.totalRow'),
    },
    {
      title: t('products.colQty'),
      dataIndex: 'quantityMilli',
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.quantityMilli - b.quantityMilli,
      render: (v: number) => <span style={NUMS}>{formatQuantityMilli(v)}</span>,
      summary: () => <span style={NUMS}>{formatQuantityMilli(totals.quantityMilli)}</span>,
    },
    {
      title: t('products.colAvgBuy'),
      dataIndex: 'avgBuyPriceKopeks',
      align: 'right',
      compact: false,
      sorter: (a, b) => (a.avgBuyPriceKopeks ?? -1) - (b.avgBuyPriceKopeks ?? -1),
      render: (v: number | null) => <Price value={v} />,
    },
    {
      title: t('products.colAvgIssue'),
      dataIndex: 'avgIssuePriceKopeks',
      align: 'right',
      compact: false,
      sorter: (a, b) => (a.avgIssuePriceKopeks ?? -1) - (b.avgIssuePriceKopeks ?? -1),
      render: (v: number | null) => <Price value={v} />,
    },
    {
      title: t('products.colBuySum'),
      dataIndex: 'buySumKopeks',
      align: 'right',
      compact: false,
      sorter: (a, b) => a.buySumKopeks - b.buySumKopeks,
      render: (v: number) => <Money value={v} />,
      summary: () => <Money value={totals.buySumKopeks} />,
    },
    {
      title: t('products.colIssueSum'),
      dataIndex: 'issueSumKopeks',
      align: 'right',
      compact: false,
      sorter: (a, b) => a.issueSumKopeks - b.issueSumKopeks,
      render: (v: number) => <Money value={v} />,
      summary: () => <Money value={totals.issueSumKopeks} />,
    },
    {
      title: t('products.colProfit'),
      dataIndex: 'profitKopeks',
      align: 'right',
      sorter: (a, b) => a.profitKopeks - b.profitKopeks,
      render: (v: number) => <Money value={v} signed />,
      summary: () => <Money value={totals.profitKopeks} signed />,
    },
    {
      title: t('products.colMargin'),
      dataIndex: 'marginBps',
      align: 'right',
      compact: false,
      sorter: (a, b) => (a.marginBps ?? -Infinity) - (b.marginBps ?? -Infinity),
      render: (v: number | null) => <span style={NUMS}>{formatBps(v)}</span>,
      summary: () => (
        <span style={NUMS}>
          {formatBps(marginBps(totals.profitKopeks, totals.pricedBuySumKopeks))}
        </span>
      ),
    },
    {
      title: t('products.colShare'),
      dataIndex: 'shareBps',
      align: 'right',
      sorter: (a, b) => a.shareBps - b.shareBps,
      render: (v: number) => <span style={NUMS}>{formatBps(v)}</span>,
      summary: () => <span style={NUMS}>{formatBps(rows.length > 0 ? 10_000 : null)}</span>,
    },
  ];
  const columns = allColumns.filter((c) => !compact || c.compact !== false);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Segmented<ProductPeriod>
        block
        value={view.period}
        onChange={changePeriod}
        options={[
          { value: 'week', label: t('products.periodWeek') },
          { value: 'month', label: t('products.periodMonth') },
          { value: 'prevMonth', label: t('products.periodPrevMonth') },
          { value: 'custom', label: t('products.periodCustom') },
        ]}
      />
      {view.period === 'custom' ? (
        <DatePicker.RangePicker
          size="large"
          style={{ width: '100%' }}
          format="DD.MM.YYYY"
          allowClear={false}
          inputReadOnly
          value={[dayjs(view.from), dayjs(view.to)]}
          // The backend caps a window at MAX_RANGE_DAYS — grey out the rest.
          disabledDate={(current, { from }) =>
            from ? Math.abs(current.diff(from, 'day')) >= MAX_RANGE_DAYS : false
          }
          onChange={(range) => {
            const [from, to] = range ?? [null, null];
            if (from && to) {
              setView({ ...view, from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') });
            }
          }}
        />
      ) : null}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Segmented<ProductStatus>
          value={view.status}
          onChange={(status) => setView({ ...view, status })}
          options={[
            { value: 'posted', label: t('products.statusPosted') },
            { value: 'all', label: t('products.statusAll') },
          ]}
        />
        <Input
          prefix={<SearchOutlined />}
          placeholder={t('products.searchPlaceholder')}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: '1 1 220px' }}
        />
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {dayjs(view.from).format('D MMMM YYYY')} – {dayjs(view.to).format('D MMMM YYYY')}
        {' · '}
        {t('products.productCount', { count: rows.length })}
      </Typography.Text>
      {similarOnly && rows.length > 0 ? (
        <Typography.Text type="warning" style={{ fontSize: 13 }}>
          {t('products.similarOnly', { query: search.trim() })}
        </Typography.Text>
      ) : null}

      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        isRangeTooLong(query.error) ? (
          <EmptyBlock description={t('products.rangeTooLong', { days: MAX_RANGE_DAYS })} />
        ) : (
          <ErrorBlock onRetry={() => void query.refetch()} />
        )
      ) : query.data.rows.length === 0 ? (
        <EmptyBlock description={t('products.empty')} hint={t('products.emptyHint')} />
      ) : rows.length === 0 ? (
        <EmptyBlock description={t('products.noMatch')} />
      ) : (
        <Card size="small" styles={{ body: { padding: 0 } }}>
          <Table<ProductAnalyticsRow>
            rowKey="key"
            size="small"
            sticky={sticky}
            columns={columns}
            dataSource={rows}
            loading={query.isFetching && query.isPlaceholderData}
            scroll={compact ? undefined : { x: 'max-content' }}
            tableLayout={compact ? 'fixed' : undefined}
            pagination={{
              pageSize: 50,
              showSizeChanger: false,
              size: 'small',
              hideOnSinglePage: true,
            }}
            showSorterTooltip={false}
            onRow={(row) => ({ onClick: () => setSelected(row), style: { cursor: 'pointer' } })}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ fontWeight: 600 }}>
                  {columns.map((c, i) => (
                    <Table.Summary.Cell key={String(c.dataIndex)} index={i} align={c.align}>
                      {c.summary?.()}
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>
      )}
      {unpricedMilli > 0 ? (
        <Typography.Text type="warning" style={{ fontSize: 13 }}>
          {t('products.unpricedHint', { qty: formatQuantityMilli(unpricedMilli) })}
        </Typography.Text>
      ) : null}
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {t('products.hint')}
      </Typography.Text>

      <ProductDrawer
        product={selected}
        params={params}
        scope={scope}
        returnTo={returnTo}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
